import { useEffect, useState } from "react";
import axios from "axios";
import type { QuizChartData, WeightUnit } from "../types/upsell.types";

type ChartDataResponse = {
  current_weight: string;
  target_weight: string;
  weight_unit: WeightUnit;
};

const EMAIL_MARKETING_SESSION_KEY = "sessionId";

const isWeightUnit = (value: string): value is WeightUnit =>
  value === "kg" || value === "lb";

export const useQuizChartData = () => {
  const [chartData, setChartData] = useState<QuizChartData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const sessionId = window.sessionStorage.getItem(
      EMAIL_MARKETING_SESSION_KEY,
    );

    if (!sessionId) {
      setIsLoading(false);
      return;
    }

    let isMounted = true;

    const fetchChartData = async () => {
      try {
        const response = await axios.get<ChartDataResponse>(
          "/wp-json/api/v1/email-marketing/chart-data",
          {
            headers: {
              "X-Session-Id": sessionId,
            },
          },
        );

        const { current_weight, target_weight, weight_unit } = response.data;
        const currentWeight = Number.parseFloat(current_weight);
        const goalWeight = Number.parseFloat(target_weight);

        if (
          isMounted &&
          Number.isFinite(currentWeight) &&
          Number.isFinite(goalWeight) &&
          isWeightUnit(weight_unit)
        ) {
          setChartData({
            currentWeight,
            goalWeight,
            unit: weight_unit,
          });
        }
      } catch (error) {
        if (
          !axios.isAxiosError(error) ||
          (error.response?.status !== 404 && error.response?.status !== 400)
        ) {
          console.error("Failed to load quiz chart data", error);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchChartData();

    return () => {
      isMounted = false;
    };
  }, []);

  return { chartData, isLoading };
};
